import { Locale } from "../Locale.ts";

/**
 * The geared clock's labels (ADR 0117), in `Manipulatives/Clock/GearedClock.tsx`. The panel's own
 * heading and description are in the `manipulatives` area.
 *
 * `clock.timeIs` is what the live region says while the digital time is shown; `clock.digitalHidden`
 * and `clock.newTimeSet` are what it says while hidden, which must not give the time away.
 */
const en = {
    "clock.faceLabel": "Clock face",
    "clock.minuteHand": "Minute hand",
    "clock.minuteHandHidden": "Time hidden",
    "clock.backMinutes": "Back {n} minutes",
    "clock.forwardMinutes": "Forward {n} minutes",
    "clock.backMinute": "Back 1 minute",
    "clock.forwardMinute": "Forward 1 minute",
    "clock.backHour": "Back 1 hour",
    "clock.forwardHour": "Forward 1 hour",
    "clock.hideDigital": "Hide digital time",
    "clock.showDigital": "Show digital time",
    "clock.timeIs": "Time: {time}",
    "clock.digitalHidden": "Digital time hidden. Read the clock.",
    "clock.newTimeSet": "New time set. Read the clock.",
    "clock.newTime": "New time",
    "clock.minuteNumbers": "Minute numbers",
} as const;

export type ClockStringKey = keyof typeof en;

const es = {
    "clock.faceLabel": "Esfera del reloj",
    "clock.minuteHand": "Minutero",
    "clock.minuteHandHidden": "Hora oculta",
    "clock.backMinutes": "Atrás {n} minutos",
    "clock.forwardMinutes": "Adelante {n} minutos",
    "clock.backMinute": "Atrás 1 minuto",
    "clock.forwardMinute": "Adelante 1 minuto",
    "clock.backHour": "Atrás 1 hora",
    "clock.forwardHour": "Adelante 1 hora",
    "clock.hideDigital": "Ocultar la hora digital",
    "clock.showDigital": "Mostrar la hora digital",
    "clock.timeIs": "Hora: {time}",
    "clock.digitalHidden": "Hora digital oculta. Lee el reloj.",
    "clock.newTimeSet": "Hora nueva puesta. Lee el reloj.",
    "clock.newTime": "Hora nueva",
    "clock.minuteNumbers": "Números de los minutos",
} satisfies Record<ClockStringKey, string>;

const fr = {
    "clock.faceLabel": "Cadran de l'horloge",
    "clock.minuteHand": "Grande aiguille",
    "clock.minuteHandHidden": "Heure cachée",
    "clock.backMinutes": "Reculer de {n} minutes",
    "clock.forwardMinutes": "Avancer de {n} minutes",
    "clock.backMinute": "Reculer d'1 minute",
    "clock.forwardMinute": "Avancer d'1 minute",
    "clock.backHour": "Reculer d'1 heure",
    "clock.forwardHour": "Avancer d'1 heure",
    "clock.hideDigital": "Cacher l'heure numérique",
    "clock.showDigital": "Afficher l'heure numérique",
    "clock.timeIs": "Heure : {time}",
    "clock.digitalHidden": "Heure numérique cachée. Lisez l'horloge.",
    "clock.newTimeSet": "Nouvelle heure affichée. Lisez l'horloge.",
    "clock.newTime": "Nouvelle heure",
    "clock.minuteNumbers": "Nombres des minutes",
} satisfies Record<ClockStringKey, string>;

export const clockStrings: Record<Locale, Record<ClockStringKey, string>> = { en, es, fr };
